import { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { createBranch, getBranch, updateBranch } from '../../api/branches';
import { getUsers } from '../../api/users';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import {
  BranchFormFields,
  EMPTY_BRANCH_FORM,
  branchToForm,
  validateBranchForm,
  buildBranchPayload,
} from '../../components/branches/BranchFormFields';

export function BranchFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [form, setForm] = useState(EMPTY_BRANCH_FORM);
  const [errors, setErrors] = useState({});
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(isEdit); // edit: branch gelene kadar
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Yönetici dropdown — aktif kullanıcılar (RİSK 4).
  useEffect(() => {
    if (!isAdmin) return;
    getUsers({ is_active: 'true' })
      .then((d) => setManagers(d.users))
      .catch(() => setManagers([]));
  }, [isAdmin]);

  // Edit modu — branch'i çek, form state'ine doldur.
  useEffect(() => {
    if (!isAdmin || !isEdit) return;
    setLoading(true);
    setLoadError('');
    getBranch(id)
      .then((d) => setForm(branchToForm(d.branch)))
      .catch((err) => {
        const status = err.response?.status;
        setLoadError(status === 404 ? 'Şube bulunamadı' : (err.response?.data?.error || 'Şube yüklenemedi'));
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, isAdmin]);

  // Rol guard — AdminOnly supervisor'ı da geçirdiğinden form route'u burada
  // admin-strict korunur (GÖREV 0 kararı — yeni guard yok, mevcut <Navigate>).
  if (!isAdmin) return <Navigate to="/settings/branches" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clientErrors = validateBranchForm(form);
    if (Object.keys(clientErrors).length) { setErrors(clientErrors); return; }
    setErrors({});
    setSubmitting(true);
    try {
      const payload = buildBranchPayload(form);
      if (isEdit) await updateBranch(id, payload);
      else await createBranch(payload);
      navigate('/settings/branches');
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;

      // 400 — express-validator { errors: [{ path/param, msg }] } → alan hataları
      const serverErrors = {};
      if (data?.errors) {
        data.errors.forEach((er) => {
          const field = er.path || er.param;
          if (field) serverErrors[field] = er.msg;
        });
      }
      if (Object.keys(serverErrors).length) {
        setErrors(serverErrors);
      } else if (status === 409) {
        setErrors({ submit: data?.error || 'Bu isimde bir şube zaten var' });
      } else if (status === 403) {
        setErrors({ submit: data?.error || 'Bu işlem için yetkiniz yok' });
      } else {
        setErrors({ submit: data?.error || 'İşlem başarısız' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">
        {isEdit ? 'Şubeyi Düzenle' : 'Yeni Şube'}
      </h1>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : loadError ? (
        <div className="text-center py-16 text-slate-400 space-y-4">
          <p>{loadError}</p>
          <Button variant="secondary" onClick={() => navigate('/settings/branches')}>
            Şube Listesine Dön
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
          <BranchFormFields form={form} errors={errors} onChange={set} managers={managers} />

          {errors.submit && (
            <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{errors.submit}</p>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button type="button" variant="secondary" onClick={() => navigate('/settings/branches')}>
              İptal
            </Button>
            <Button type="submit" loading={submitting}>
              {isEdit ? 'Güncelle' : 'Kaydet'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
